import { useRef } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { deleteRole } from 'features/management/api/deleteRole';
import { getErrorMessage } from 'shared/utils/errorMessage';
import { showErrorToastWithRetry } from 'shared/utils/showErrorToastWithRetry';
import { managementKeys } from '../../api/queryKeys';

/**
 * Mutation hook for deleting a role.
 * Retries automatically on 5xx up to 3 times. Shows retry toast on final 5xx failure.
 *
 * @param {{ onSuccess?: () => void }} options
 * @returns {{ deleteMutation: { mutate: (id: number) => void, isLoading: boolean } }}
 */
const useDeleteRole = ({ onSuccess } = {}) => {
  const queryClient = useQueryClient();
  const lastDeletedIdRef = useRef(null);

  const deleteMutation = useMutation(
    (id) => {
      lastDeletedIdRef.current = id;
      return deleteRole(id);
    },
    {
      retry: (failureCount, error) => {
        const status = error?.status || error?.response?.status || 500;
        return status >= 500 && failureCount < 3;
      },
      retryDelay: 3000,
      onSuccess: (res) => {
        toast.success(res?.message || 'Role deleted');
        queryClient.invalidateQueries(managementKeys.roles());
        onSuccess?.();
      },
      onError: (error) => {
        const status = error?.status || error?.response?.status || 500;
        const message = getErrorMessage(error);

        if (status >= 500) {
          showErrorToastWithRetry(`Delete role: ${message}`, () =>
            deleteMutation.mutate(lastDeletedIdRef.current)
          );
        } else {
          toast.error(`Delete role: ${message}`);
        }
      },
    }
  );

  return { deleteMutation };
};

export default useDeleteRole;
